<script setup>
import {ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance} from 'vue'
import {Editor, EditorContent} from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Paragraph from '@tiptap/extension-paragraph'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Heading from '@tiptap/extension-heading'
import Image from '@tiptap/extension-image'
import {TextStyle} from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import BulletList from '@tiptap/extension-bullet-list'
import ListItem from '@tiptap/extension-list-item'
import {NodeSelection} from "@tiptap/pm/state"

// Local Components
import TiptapToolbar from "./TiptapToolbar.vue"
import TipTapImageSelectionModal from "../../vue/modals/TipTapImageSelectionModal.vue"
import TiptapFooter from "./TiptapFooter.vue"

const props = defineProps({
    value: {type: String, required: true},
    formId: {type: String, required: true}
})

const emit = defineEmits(['input'])

const {proxy} = getCurrentInstance()
const store = proxy.$store

// --- State ---
const editor = ref(null)
const isSourceMode = ref(false)
const isFullScreen = ref(false)
const rawHtml = ref('')
const showImageModal = ref(false)
const activeImageAttrs = ref(null)
const pasteAsPlainTextActive = ref(false)
const isTouched = ref(false)

// --- Computed ---
const cssFramework = computed(() => store.state.settings.config.css_framework || 'bootstrap@3.3.7')
const isBootstrap = computed(() => cssFramework.value.startsWith('bootstrap'))
const apiHost = computed(() => store.state.ccmsSchemeAndHost)

// --- Watchers ---
watch(() => props.value, (newValue) => {
    if (!editor.value) return

    const currentHTML = editor.value.getHTML()
    if (newValue === currentHTML) return

    editor.value.commands.setContent(newValue, false)
})

// --- Methods ---
const formatHtml = (html) => {
    let tab = '  '
    let result = ''
    let indent = ''
    html.split(/>\s*</).forEach((element) => {
        if (element.match(/^\/\w/)) indent = indent.substring(tab.length)
        result += indent + '<' + element + '>\n\n'
        if (element.match(/^<?\w[^>]*[^\/]$/) && !element.startsWith("input") && !element.startsWith("img") && !element.startsWith("br")) {
            indent += tab
        }
    })
    return result.substring(1, result.length - 3)
}

const toggleSource = () => {
    if (isSourceMode.value) {
        editor.value.commands.setContent(rawHtml.value, true)
    } else {
        rawHtml.value = formatHtml(editor.value.getHTML())
    }
    isSourceMode.value = !isSourceMode.value
}

const openImageModal = () => {
    const {selection} = editor.value.state
    if (selection.node && selection.node.type.name === 'image') {
        activeImageAttrs.value = selection.node.attrs
    }
    showImageModal.value = true
}

const closeImageModal = () => {
    showImageModal.value = false
    activeImageAttrs.value = null
}

// --- Lifecycle (Initialization) ---
onMounted(() => {
    const blockquoteOptions = isBootstrap.value ? {HTMLAttributes: {class: 'blockquote'}} : {}

    editor.value = new Editor({
        content: props.value,
        autofocus: false, // Prevents Tiptap from grabbing initialization selections
        extensions: [
            StarterKit.configure({
                paragraph: false,
                blockquote: blockquoteOptions,
                bulletList: false,
                listItem: false
            }),
            Paragraph.extend({
                addAttributes() {
                    return {class: {default: null}}
                }
            }),
            BulletList.extend({
                addAttributes() {
                    return {
                        class: {
                            default: null,
                            // Tells Tiptap how to read the class from the incoming database HTML string
                            parseHTML: el => el.getAttribute('class'),
                            // Tells Tiptap how to write the class back to the output DOM / saved string
                            renderHTML: attrs => attrs.class ? {class: attrs.class} : {}
                        }
                    }
                }
            }),
            ListItem.extend({
                content: '(paragraph|heading) block*',
                addKeyboardShortcuts() {
                    return {
                        // 1. Explicitly preserve the standard Enter behavior: Create a new bullet point
                        'Enter': () => this.editor.commands.splitListItem(this.name),

                        // 2. Map Shift+Enter to strictly create an internal paragraph block
                        'Shift-Enter': () => this.editor.commands.splitBlock(),
                    }
                },
            }),
            TextStyle, Color,
            Highlight.configure({multicolor: true}),
            TextAlign.configure({types: ['heading', 'paragraph']}),
            Image.extend({
                inline: true,
                // Without this, "inline: true" will generate the error:
                // "SyntaxError: Mixing inline and block content (in content expression 'block+')"
                group: 'inline',
                addAttributes() {
                    return {
                        src: {
                            parseHTML: el => el.getAttribute('src'),
                            renderHTML: attrs => {
                                let src = attrs.src
                                if (src && src.startsWith(apiHost.value)) src = src.replace(apiHost.value, '')
                                return {src}
                            }
                        },
                        alt: {default: null},
                        title: {default: null},
                        width: {default: null},
                        height: {default: null},
                        sizes: {
                            default: null,
                            renderHTML: attrs => attrs.sizes ? {sizes: attrs.sizes} : {}
                        },
                        class: {default: null},
                        style: {
                            default: null,
                            parseHTML: el => {
                                const inline = el.getAttribute('style')
                                if (inline) return inline
                                const cls = el.getAttribute('class') || ''
                                if (cls.includes('text-editor-image-left')) return 'display: block; max-width: 100%; float: left; margin: 0 1rem 1rem 0;'
                                if (cls.includes('text-editor-image-right')) return 'display: block; max-width: 100%; float: right; margin: 0 0 1rem 1rem;'
                                if (cls.includes('text-editor-image-center')) return 'display: block; max-width: 100%; margin: 0 auto 1rem auto;'
                                return null
                            },
                            renderHTML: attrs => attrs.style ? {style: attrs.style} : {}
                        }
                    }
                },
                addNodeView() {
                    return ({node, view, getPos}) => {
                        const img = document.createElement('img')

                        // 2. Attach the click handler directly to the image
                        img.addEventListener('click', (event) => {
                            // Stop the link click-through behavior
                            event.preventDefault()
                            event.stopPropagation()

                            // Handle the selection natively inside the editor
                            if (typeof getPos === 'function') {
                                const {state, dispatch} = view
                                const {NodeSelection} = require('@tiptap/pm/state')
                                const transaction = state.tr.setSelection(
                                    NodeSelection.create(state.doc, getPos())
                                )
                                dispatch(transaction)
                            }
                        })

                        const sync = (attrs) => {
                            Object.entries(attrs).forEach(([key, value]) => {
                                if (value === null || value === undefined) {
                                    img.removeAttribute(key)
                                    return
                                }
                                let finalValue = value
                                if (key === 'src' && !value.startsWith('http')) finalValue = apiHost.value + value
                                if (img.getAttribute(key) !== String(finalValue)) img.setAttribute(key, finalValue)
                            })
                        }

                        sync(node.attrs)

                        // 3. Return the image itself as the primary DOM node
                        return {
                            dom: img,
                            update: (updatedNode) => {
                                if (updatedNode.type.name !== 'image') return false
                                sync(updatedNode.attrs)
                                return true
                            }
                        }
                    }
                }
            }),
            Heading.extend({
                addAttributes() {
                    return {
                        class: {
                            default: null,
                            parseHTML: el => el.getAttribute('class'),
                            renderHTML: attrs => attrs.class ? {class: attrs.class} : {}
                        }
                    }
                }
            }).configure({levels: [1, 2, 3, 4, 5, 6]}),
            Link.extend({
                addAttributes() {
                    return {
                        ...this.parent?.(),
                        class: {default: null},
                        target: {
                            default: null,
                            renderHTML: attributes => {
                                const href = attributes.href || ''
                                const isInternal = isInternalUrl(href)
                                return isInternal
                                    // To ensure that "target" is removed when changing an existing absolute URL which
                                    // already has target="_blank", {target: null} must be returned and not simply
                                    // null - as this won't modify an existing target attribute.
                                    ? {target: null}
                                    : {target: '_blank'}
                            }
                        },
                        rel: {
                            default: null,
                            renderHTML: attributes => {
                                const href = attributes.href || ''
                                const isInternal = isInternalUrl(href)
                                return isInternal ? {rel: 'noopener'} : {rel: 'noopener noreferrer nofollow'}
                            }
                        }
                    }
                }
            }).configure({
                openOnClick: false,
                autolink: true,
                linkOnPaste: true,
                validate: href => !!href,
                HTMLAttributes: {
                    //target: null, // Omits the "target" key
                }
            }),
        ],
        editorProps: {
            attributes: {class: 'editor-content'},
            handleKeyDown: (view, e) => {
                if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'u') {
                    e.preventDefault()
                    return true
                }
                return false
            },
            handlePaste: (view, event) => {
                // If the toggle button isn't active, let our normal HTML sanitation handle it
                if (!pasteAsPlainTextActive.value) {
                    return false
                }

                // 1. Force extract ONLY the plain text from the clipboard
                let text = event.clipboardData.getData('text/plain')

                if (text) {
                    // 2. Clean up awkward line breaks/double spaces
                    const cleanText = text.replace(/\u200B/g, "").trim()

                    const {state, dispatch} = view
                    const {from, to} = state.selection

                    // FIX 2: Replace the selected text range cleanly
                    const transaction = state.tr.insertText(cleanText, from, to)
                    dispatch(transaction)

                    // Return true to tell Tiptap we completely handled this event
                    return true
                }

                return false
            },
            handleDOMEvents: {
                dblclick: (view, event) => {
                    if (event.target?.nodeName === 'IMG') {
                        openImageModal()
                        return true
                    }
                    return false
                }
            }
        },
        onFocus: () => {
            if (!isTouched.value) isTouched.value = true
        },
        // Add this native event gatekeeper here:
        onCreate: ({editor: currentEditor}) => {
            const domElement = currentEditor.options.element
            if (!domElement) return

            // Shared handler to completely neutralize navigation behavior
            const killLinkNavigation = (event) => {
                const target = event.target
                const closestLink = target?.closest('a')

                if (closestLink) {
                    // Instantly kill the redirect thread at the earliest possible phase
                    event.preventDefault()
                    event.stopPropagation()

                    // If it's a click event on an image, handle the visual selection border
                    if (event.type === 'click' && target && target.nodeName === 'IMG') {
                        const view = currentEditor.view
                        const pos = view.posAtDOM(target, 0)
                        if (pos !== undefined && pos >= 0) {
                            const {state, dispatch} = view
                            const transaction = state.tr.setSelection(NodeSelection.create(state.doc, pos))
                            dispatch(transaction)
                        }
                    }
                }
            }

            // Intercept BOTH events at the capture phase (true) before they can bubble or execute
            domElement.addEventListener('click', killLinkNavigation, {capture: true})
            domElement.addEventListener('pointerup', killLinkNavigation, {capture: true})
            domElement.addEventListener('mouseup', killLinkNavigation, {capture: true})
        },
        onUpdate: () => {
            let html = editor.value.getHTML()
            const host = apiHost.value
            const escapedHost = host.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            html = html.replace(new RegExp(escapedHost, 'g'), '').replace(/<p><\/p>$/, '')
            emit('input', html)
        }
    })
})

onBeforeUnmount(() => {
    if (editor.value) {
        // 1. Manually blur to stop ProseMirror's cursor tracking
        editor.value.commands.blur()

        // 2. Wrap destruction in a try/catch to silence the Tiptap view error
        // and defer it to the next event loop tick.
        const instance = editor.value

        // Clear the reactive ref immediately so sub-components (Toolbar/Footer)
        // stop trying to read the editor state.
        editor.value = null

        setTimeout(() => {
            try {
                instance.destroy()
            } catch (e) {
                // Tiptap sometimes throws if the DOM node was already
                // yanked out by Vue's route transition.
                console.warn('Tiptap cleanup handled safely.')
            }
        }, 0)
    }
})

// Helper function to check if a URL is internal
function isInternalUrl(href) {
    if (!href) return false

    // Relative links and placeholder tokens
    if (
        href.startsWith('/') ||
        href.startsWith('#') ||
        href.startsWith('[PID:') ||
        href.startsWith('[CID:')
    ) {
        return true
    }

    return false
}


</script>

<template>
    <div v-if="editor" class="editor-wrapper" :class="{'tiptap-fullscreen': isFullScreen}">
        <TiptapToolbar
            v-if="isTouched"
            :editor="editor"
            @toggle-source="toggleSource"
            @toggle-full-screen="isFullScreen = !isFullScreen"
            @open-image-modal="openImageModal"
            :is-full-screen="isFullScreen"
            :is-source-mode="isSourceMode"
            :paste-as-plain-text-active.sync="pasteAsPlainTextActive"
        />

        <editor-content v-show="!isSourceMode" :editor="editor" class="flex-fill overflow-y-auto"/>

        <TiptapFooter v-if="isTouched" v-show="!isSourceMode" :editor="editor"/>

        <textarea
            v-show="isSourceMode"
            v-model="rawHtml"
            class="form-control source-mode-textarea small flex-fill"
        />

        <TipTapImageSelectionModal
            v-if="showImageModal"
            :form-id="formId"
            :editor="editor"
            :image="activeImageAttrs"
            @hide-modal="closeImageModal"
        />
    </div>
</template>

<style scoped lang="scss">
/* Styles remain identical to your original SCSS */
$editor-primary: #0d6efd;
$editor-border: var(--bs-border-color);

.editor-wrapper {
    position: relative;
    border: 1px solid $editor-border;
    border-radius: 0.375rem;
    background-color: #fff;

    ::v-deep {
        // Targets your TiptapToolbar component wrapper
        .tiptap-toolbar {
            position: sticky;
            // This was disabled because it caused top margin in Safari but
            // without it sticky doesn't work.
            top: 0 !important;
            z-index: 10;
        }

        .ProseMirror {
            padding: 0.75rem;
            min-height: 200px;
            outline: none !important;

            // Prevents <a> links from opening their target URL.
            a {
                pointer-events: none; /* Prevents the cursor from triggering browser link redirects */
                cursor: text; /* Forces the cursor to look normal so users can highlight text link ranges */
            }

            img {
                outline: 6px solid transparent;
                outline-offset: -6px;
                display: inline-block;
                transition: outline 0.1s ease-in-out;
                // Re-enable disabled links for images, so images can be selected.
                pointer-events: auto;

                &.ProseMirror-selectednode {
                    outline: 3px solid $editor-primary !important;
                    outline-offset: 2px;
                }
            }
        }

        .source-mode-textarea {
            display: block;
            width: 100%;
            padding: 0.75rem;
            background-color: #f8f9fa;
            font-family: monospace;
            min-height: 200px;
            font-size: 13px;
            field-sizing: content;
            /* field-sizing: content; causes issues with the resize drag handle, so disable it completely */
            resize: none;
        }
    }

    &.tiptap-fullscreen {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1050;
        display: flex;
        flex-direction: column;
        background: #fff;
    }
}

[data-bs-theme="dark"] {
    .editor-wrapper {
        background-color: black;

        ::v-deep {
            .tiptap-toolbar {
                background: #000;
            }

            .source-mode-textarea {
                background-color: #000;
            }
        }
    }
}
</style>